"use client";

import React from "react";
import { Calendar, User, ArrowLeft, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function PostDetailConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="h-6 w-32 animate-pulse bg-muted mb-6" />
        <div className="h-12 w-3/4 animate-pulse bg-muted mb-4" />
        <div className="h-5 w-48 animate-pulse bg-muted mb-8" />
        <div className="aspect-16/9 w-full animate-pulse bg-muted mb-10" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse bg-muted" />
          <div className="h-4 w-5/6 animate-pulse bg-muted" />
          <div className="h-4 w-4/6 animate-pulse bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <p className="text-destructive font-medium">Unable to load article content.</p>
        <div className="mt-4">
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const post = posts && posts.length > 0 ? posts[0] : null;

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <h2 className="text-xl font-bold font-serif uppercase tracking-wider text-foreground">
          Article not found
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The requested article may have been moved or removed from our archive.
        </p>
        <div className="mt-6">
          <Button asChild variant="outline">
            <Link to="/SetupSafetyAndMaintenancePage">
              <ArrowLeft className="size-4 mr-2" />
              Return to guide index
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const authorName = post.author?.name || "Technical Team";
  const authorAvatar =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"] ||
    "";
  const authorInitials = authorName
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <article className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Back link navigation */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <Link
          to="/SetupSafetyAndMaintenancePage"
          className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-1.5 size-4" />
          Technical library
        </Link>
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.categories.map((cat, i) => (
              <span
                key={cat.id || i}
                data-index={i}
                className="inline-flex items-center gap-1 bg-muted px-2.5 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground"
              >
                <Tag className="size-3" />
                {cat.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Prominent Title */}
      <header className="mb-8">
        <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl uppercase leading-tight">
          {post.title}
        </h1>

        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-mono uppercase text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <User className="size-3.5 text-primary" />
            <span>{authorName}</span>
          </div>
          {formattedDate && (
            <>
              <span className="text-border">|</span>
              <div className="flex items-center gap-1.5">
                <Calendar className="size-3.5 text-primary" />
                <time dateTime={post.date}>{formattedDate}</time>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="mb-10 overflow-hidden border border-border bg-muted">
          <img
            src={post.featuredImage}
            alt={post.title || "Featured article image"}
            loading="lazy"
            decoding="async"
            className="w-full object-cover max-h-[520px]"
          />
        </div>
      )}

      {/* Main Post Content */}
      <div className="text-foreground leading-relaxed">
        {post.content ? (
          <div
            className="prose prose-neutral max-w-none space-y-6 [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:text-foreground [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:text-base [&_p]:leading-7 [&_p]:text-foreground/90 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_code]:font-mono [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-xs [&_pre]:bg-secondary [&_pre]:text-secondary-foreground [&_pre]:p-4 [&_pre]:overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : post.excerpt ? (
          <p className="text-lg leading-8 text-foreground/90">{post.excerpt}</p>
        ) : (
          <p className="text-base text-muted-foreground">
            No text content available for this entry.
          </p>
        )}
      </div>

      <Separator className="my-10" />

      {/* Author Information Section */}
      <Card className="border border-border bg-card p-6 shadow-xs">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <Avatar className="size-16 shrink-0 border border-border">
              {authorAvatar ? (
                <AvatarImage src={authorAvatar} alt={authorName} />
              ) : null}
              <AvatarFallback className="bg-secondary text-secondary-foreground font-mono text-base font-bold">
                {authorInitials || "MB"}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-xs font-mono uppercase tracking-widest text-primary font-semibold">
                    Technical author
                  </span>
                  <h3 className="font-serif text-xl font-bold uppercase tracking-tight text-card-foreground">
                    {authorName}
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (typeof navigator !== "undefined" && navigator.clipboard) {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  className="gap-1.5 text-xs font-mono"
                >
                  <Share2 className="size-3.5" />
                  Copy link
                </Button>
              </div>

              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Industrial mechanical equipment specialist providing commercial
                operators with verified safety protocols, maintenance regimens,
                and technical operating guidelines.
              </p>

              <div className="mt-4 flex flex-wrap gap-4 pt-2 border-t border-border/60">
                <Link
                  to="/ContactPage"
                  className="text-xs font-mono uppercase tracking-wider text-primary hover:underline font-semibold"
                >
                  Request technical consultation
                </Link>
                <Link
                  to="/ShippingWarrantyAndOrderingPage"
                  className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground"
                >
                  Support documentation
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

export default function SinglePost() {
  const currentWPQueryParams =
    typeof wvcClient !== "undefined" && wvcClient.getCurrentWPQueryParams
      ? wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground min-h-[50vh] py-8 sm:py-12"
    >
      <PostsProvider wp_query={wp_query}>
        <PostDetailConsumer />
      </PostsProvider>
    </section>
  );
}